import React, { useState, useMemo } from 'react';

const FamilyCalendar = ({ gedcomData, onClose }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming', 'month', 'year', 'timeline'
  const [selectedEventType, setSelectedEventType] = useState('all');

  // Extract all important dates from GEDCOM data
  const extractDates = useMemo(() => {
    if (!gedcomData) return [];
    
    
    const { individuals, families } = gedcomData;
    const events = [];


    // Extract birth and death dates from individuals
    if (individuals) {
      Object.entries(individuals).forEach(([id, person]) => {
        
        if (person.birth) {
          const birthDate = parseDate(person.birth);
          if (birthDate) {
            events.push({
              id: `birth-${id}`,
              type: 'birth',
              date: birthDate,
              person: person.name,
              description: `${person.name} was born`,
              location: person.birthPlace || '',
              icon: '🎂'
            });
          }
        }

        if (person.death) {
          const deathDate = parseDate(person.death);
          if (deathDate) {
            events.push({
              id: `death-${id}`,
              type: 'death',
              date: deathDate,
              person: person.name,
              description: `${person.name} passed away`,
              location: person.deathPlace || '',
              icon: '🕊️'
            });
          }
        }
      });
    }

    // Extract marriage dates from families
    if (families) {
      Object.entries(families).forEach(([id, family]) => {
        
        if (family.marriage) {
          const marriageDate = parseDate(family.marriage);
          if (marriageDate) {
            const husband = individuals?.[family.husband]?.name || 'Unknown';
            const wife = individuals?.[family.wife]?.name || 'Unknown';
            events.push({
              id: `marriage-${id}`,
              type: 'marriage',
              date: marriageDate,
              person: `${husband} & ${wife}`,
              description: `${husband} married ${wife}`,
              location: family.marriagePlace || '',
              icon: '💒'
            });
          }
        }
      });
    }


    return events.sort((a, b) => a.date - b.date);
  }, [gedcomData]);

  // Parse date string to Date object
  function parseDate(dateStr) {
    if (!dateStr) return null;
    
    
    // Handle various date formats
    const patterns = [
      /(\d{1,2})\s+(\w+)\s+(\d{4})/,  // "15 MAR 1920"
      /(\w+)\s+(\d{1,2}),?\s+(\d{4})/, // "March 15, 1920"
      /(\d{4})-(\d{1,2})-(\d{1,2})/,   // "1920-03-15"
      /(\d{1,2})\/(\d{1,2})\/(\d{4})/,  // "03/15/1920"
      /(\d{4})/                         // "1920" (year only)
    ];

    const monthNames = {
      'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
      'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11,
      'JANUARY': 0, 'FEBRUARY': 1, 'MARCH': 2, 'APRIL': 3, 'JUNE': 5,
      'JULY': 6, 'AUGUST': 7, 'SEPTEMBER': 8, 'OCTOBER': 9, 'NOVEMBER': 10, 'DECEMBER': 11
    };

    for (let i = 0; i < patterns.length; i++) {
      const pattern = patterns[i];
      const match = dateStr.toUpperCase().match(pattern);
      if (match) {
        
        if (pattern === patterns[0]) { // "15 MAR 1920"
          const day = parseInt(match[1]);
          const month = monthNames[match[2]];
          const year = parseInt(match[3]);
          if (month !== undefined) {
            const result = new Date(year, month, day);
            return result;
          }
        } else if (pattern === patterns[1]) { // "March 15, 1920"
          const month = monthNames[match[1].toUpperCase()];
          const day = parseInt(match[2]);
          const year = parseInt(match[3]);
          if (month !== undefined) {
            const result = new Date(year, month, day);
            return result;
          }
        } else if (pattern === patterns[2]) { // "1920-03-15"
          const result = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
          return result;
        } else if (pattern === patterns[3]) { // "03/15/1920"
          const result = new Date(parseInt(match[3]), parseInt(match[1]) - 1, parseInt(match[2]));
          return result;
        } else if (pattern === patterns[4]) { // "1920"
          const result = new Date(parseInt(match[1]), 0, 1);
          return result;
        }
      }
    }
    
    return null;
  }

  // Calculate anniversaries for the current year
  const calculateAnniversaries = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const anniversaries = [];

    extractDates.forEach(event => {
      const originalYear = event.date.getFullYear();
      const yearsAgo = currentYear - originalYear;
      
      // Only show anniversaries for events that happened in the past
      if (yearsAgo > 0) {
        const anniversaryDate = new Date(currentYear, event.date.getMonth(), event.date.getDate());
        
        let anniversaryDescription = '';
        let anniversaryIcon = '';
        
        if (event.type === 'birth') {
          anniversaryDescription = `${event.person}'s ${yearsAgo}${getOrdinalSuffix(yearsAgo)} birthday`;
          anniversaryIcon = '🎉';
        } else if (event.type === 'death') {
          anniversaryDescription = `${yearsAgo} years since ${event.person} passed away`;
          anniversaryIcon = '🌹';
        } else if (event.type === 'marriage') {
          anniversaryDescription = `${event.person}'s ${yearsAgo}${getOrdinalSuffix(yearsAgo)} wedding anniversary`;
          anniversaryIcon = '💕';
        }
        
        anniversaries.push({
          id: `anniversary-${event.id}`,
          type: `${event.type}-anniversary`,
          date: anniversaryDate,
          person: event.person,
          description: anniversaryDescription,
          location: event.location,
          icon: anniversaryIcon,
          yearsAgo: yearsAgo,
          originalDate: event.date
        });
      }
    });

    return anniversaries;
  }, [extractDates]);

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  function getOrdinalSuffix(num) {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  // Combine original events with anniversaries
  const allEvents = useMemo(() => {
    return [...extractDates, ...calculateAnniversaries].sort((a, b) => a.date - b.date);
  }, [extractDates, calculateAnniversaries]);

  // Filter events based on selected type
  const filteredEvents = useMemo(() => {
    if (selectedEventType === 'all') return allEvents;
    if (selectedEventType === 'anniversaries') {
      return allEvents.filter(event => event.type.includes('anniversary'));
    }
    if (selectedEventType === 'historical') {
      return allEvents.filter(event => !event.type.includes('anniversary'));
    }
    return allEvents.filter(event => event.type === selectedEventType || event.type === `${selectedEventType}-anniversary`);
  }, [allEvents, selectedEventType]);

  // Early return after all hooks
  if (!gedcomData) return null;

  // Get upcoming anniversaries (next 30 days)
  const getUpcomingAnniversaries = () => {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    
    return calculateAnniversaries.filter(anniversary => {
      return anniversary.date >= today && anniversary.date <= thirtyDaysFromNow;
    }).sort((a, b) => a.date - b.date);
  };

  // Get today's anniversaries
  const getTodayAnniversaries = () => {
    const today = new Date();
    return calculateAnniversaries.filter(anniversary => {
      return anniversary.date.getDate() === today.getDate() &&
             anniversary.date.getMonth() === today.getMonth();
    });
  };

  // Get this month's anniversaries
  const getThisMonthAnniversaries = () => {
    const today = new Date();
    return calculateAnniversaries.filter(anniversary => {
      return anniversary.date.getMonth() === today.getMonth();
    }).sort((a, b) => a.date.getDate() - b.date.getDate());
  };

  // Render upcoming anniversaries view
  const renderUpcomingView = () => {
    const upcomingAnniversaries = getUpcomingAnniversaries();
    const todayAnniversaries = getTodayAnniversaries();
    const thisMonthAnniversaries = getThisMonthAnniversaries();
    
    return (
      <div className="upcoming-view">
        <div className="upcoming-header">
          <h3>Family Celebrations & Remembrances</h3>
          <p>Important dates coming up and happening now</p>
        </div>

        <div className="upcoming-content">
          <div className="upcoming-sections">
            {/* Today's Anniversaries */}
            {todayAnniversaries.length > 0 && (
              <div className="today-section">
                <h4><i className="fas fa-star"></i> Today's Special Days</h4>
                <div className="anniversary-grid">
                  {todayAnniversaries.map(anniversary => (
                    <div key={anniversary.id} className={`anniversary-card today ${anniversary.type}`}>
                      <div className="anniversary-icon-large">{anniversary.icon}</div>
                      <div className="anniversary-content">
                        <h5>{anniversary.person}</h5>
                        <p className="anniversary-desc">{anniversary.description}</p>
                        <div className="anniversary-meta">
                          <span className="years-badge">{anniversary.yearsAgo} years ago</span>
                          {anniversary.location && (
                            <span className="location-badge">
                              <i className="fas fa-map-marker-alt"></i> {anniversary.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Next 7 Days */}
            {upcomingAnniversaries.filter(a => {
              const daysFromNow = Math.ceil((a.date - new Date()) / (1000 * 60 * 60 * 24));
              return daysFromNow >= 0 && daysFromNow <= 7;
            }).length > 0 && (
              <div className="week-section">
                <h4><i className="fas fa-calendar-week"></i> This Week (Next 7 Days)</h4>
                <div className="anniversary-list">
                  {upcomingAnniversaries
                    .filter(a => {
                      const daysFromNow = Math.ceil((a.date - new Date()) / (1000 * 60 * 60 * 24));
                      return daysFromNow >= 0 && daysFromNow <= 7;
                    })
                    .map(anniversary => (
                      <div key={anniversary.id} className={`anniversary-item ${anniversary.type}`}>
                        <div className="anniversary-date">
                          <div className="anniversary-icon">{anniversary.icon}</div>
                          <div className="date-info">
                            <div className="anniversary-day">{anniversary.date.getDate()}</div>
                            <div className="anniversary-month">
                              {anniversary.date.toLocaleDateString('en-US', { month: 'short' })}
                            </div>
                            <div className="days-until">
                              {Math.ceil((anniversary.date - new Date()) / (1000 * 60 * 60 * 24)) === 0 
                                ? 'Today' 
                                : `${Math.ceil((anniversary.date - new Date()) / (1000 * 60 * 60 * 24))} days`}
                            </div>
                          </div>
                        </div>
                        <div className="anniversary-details">
                          <div className="anniversary-description">{anniversary.description}</div>
                          <div className="anniversary-years">{anniversary.yearsAgo} years ago</div>
                          {anniversary.location && (
                            <div className="anniversary-location">
                              <i className="fas fa-map-marker-alt"></i> {anniversary.location}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* This Month */}
            <div className="month-section">
              <h4><i className="fas fa-calendar-alt"></i> This Month ({new Date().toLocaleDateString('en-US', { month: 'long' })})</h4>
              <div className="anniversary-list">
                {thisMonthAnniversaries.slice(0, 15).map(anniversary => (
                  <div key={anniversary.id} className={`anniversary-item ${anniversary.type}`}>
                    <div className="anniversary-date">
                      <div className="anniversary-icon">{anniversary.icon}</div>
                      <div className="date-info">
                        <div className="anniversary-day">{anniversary.date.getDate()}</div>
                        <div className="anniversary-month">
                          {anniversary.date.toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </div>
                    </div>
                    <div className="anniversary-details">
                      <div className="anniversary-description">{anniversary.description}</div>
                      <div className="anniversary-years">{anniversary.yearsAgo} years ago</div>
                      {anniversary.location && (
                        <div className="anniversary-location">
                          <i className="fas fa-map-marker-alt"></i> {anniversary.location}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {thisMonthAnniversaries.length > 15 && (
                <div className="show-more">
                  <p>And {thisMonthAnniversaries.length - 15} more this month...</p>
                </div>
              )}
            </div>
          </div>

          {/* Legend for Upcoming View */}
          <div className="calendar-legend">
            <div className="legend-items">
              <div className="legend-item">
                <span className="legend-icon birth">🎂</span>
                <span>Births</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon death">🕊️</span>
                <span>Deaths</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon marriage">💒</span>
                <span>Marriages</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon birth-anniversary">🎉</span>
                <span>Birthdays</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon death-anniversary">🌹</span>
                <span>Memorials</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon marriage-anniversary">💕</span>
                <span>Wedding Anniversaries</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get events for current month view
  const getMonthEvents = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    return filteredEvents.filter(event => {
      return event.date.getFullYear() === year && event.date.getMonth() === month;
    });
  };

  // Get events for current year view
  const getYearEvents = () => {
    const year = currentDate.getFullYear();
    return filteredEvents.filter(event => event.date.getFullYear() === year);
  };

  // Generate calendar days for month view
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const monthEvents = getMonthEvents();

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayEvents = monthEvents.filter(event => 
        event.date.getDate() === date.getDate() &&
        event.date.getMonth() === date.getMonth() &&
        event.date.getFullYear() === date.getFullYear()
      );

      days.push({
        date: new Date(date),
        isCurrentMonth: date.getMonth() === month,
        events: dayEvents
      });
    }

    return days;
  };

  // Navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateYear = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Render month view
  const renderMonthView = () => {
    const days = generateCalendarDays();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return (
      <div className="calendar-month-view">
        <div className="calendar-header">
          <button onClick={() => navigateMonth(-1)} className="nav-btn">
            <i className="fas fa-chevron-left"></i>
          </button>
          <h3>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
          <button onClick={() => navigateMonth(1)} className="nav-btn">
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>

        <div className="calendar-days">
          {days.map((day, index) => (
            <div
              key={index}
              className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${
                day.events.length > 0 ? 'has-events' : ''
              }`}
            >
              <div className="day-number">{day.date.getDate()}</div>
              {day.events.length > 0 && (
                <div className="day-events">
                  {day.events.slice(0, 3).map(event => (
                    <div key={event.id} className={`event-dot ${event.type}`} title={event.description}>
                      {event.icon}
                    </div>
                  ))}
                  {day.events.length > 3 && (
                    <div className="more-events">+{day.events.length - 3}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render year view
  const renderYearView = () => {
    const yearEvents = getYearEvents();
    const eventsByMonth = {};

    yearEvents.forEach(event => {
      const month = event.date.getMonth();
      if (!eventsByMonth[month]) eventsByMonth[month] = [];
      eventsByMonth[month].push(event);
    });

    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return (
      <div className="calendar-year-view">
        <div className="calendar-header">
          <button onClick={() => navigateYear(-1)} className="nav-btn">
            <i className="fas fa-chevron-left"></i>
          </button>
          <h3>{currentDate.getFullYear()}</h3>
          <button onClick={() => navigateYear(1)} className="nav-btn">
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        <div className="year-months">
          {monthNames.map((month, index) => (
            <div
              key={month}
              className={`year-month ${eventsByMonth[index] ? 'has-events' : ''}`}
              onClick={() => {
                const newDate = new Date(currentDate);
                newDate.setMonth(index);
                setCurrentDate(newDate);
                setViewMode('month');
              }}
            >
              <div className="month-name">{month}</div>
              <div className="month-event-count">
                {eventsByMonth[index] ? eventsByMonth[index].length : 0} events
              </div>
              {eventsByMonth[index] && (
                <div className="month-events-preview">
                  {eventsByMonth[index].slice(0, 3).map(event => (
                    <div key={event.id} className={`event-preview ${event.type}`}>
                      {event.icon} {event.person}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render timeline view
  const renderTimelineView = () => {
    const timelineEvents = filteredEvents.slice(0, 50); // Limit for performance

    return (
      <div className="calendar-timeline-view">
        <div className="timeline-header">
          <h3>Family Timeline</h3>
          <p>{filteredEvents.length} total events</p>
        </div>

        <div className="timeline-events">
          {timelineEvents.map(event => (
            <div key={event.id} className={`timeline-event ${event.type}`}>
              <div className="event-date">
                <div className="event-icon">{event.icon}</div>
                <div className="date-info">
                  <div className="event-day">{event.date.getDate()}</div>
                  <div className="event-month">
                    {event.date.toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                  <div className="event-year">{event.date.getFullYear()}</div>
                </div>
              </div>
              <div className="event-details">
                <div className="event-description">{event.description}</div>
                {event.location && (
                  <div className="event-location">
                    <i className="fas fa-map-marker-alt"></i> {event.location}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="family-calendar-overlay">
      <div className="family-calendar-container">
        <div className="calendar-controls">
          <div className="calendar-title">
            <h2><i className="fas fa-calendar-alt"></i> Rossetti Family Calendar</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>

          <div className="calendar-filters">
            <div className="view-modes">
              <button
                className={`mode-btn ${viewMode === 'upcoming' ? 'active' : ''}`}
                onClick={() => setViewMode('upcoming')}
              >
                Upcoming
              </button>
              <button
                className={`mode-btn ${viewMode === 'month' ? 'active' : ''}`}
                onClick={() => setViewMode('month')}
              >
                Month
              </button>
              <button
                className={`mode-btn ${viewMode === 'year' ? 'active' : ''}`}
                onClick={() => setViewMode('year')}
              >
                Year
              </button>
              <button
                className={`mode-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                onClick={() => setViewMode('timeline')}
              >
                Timeline
              </button>
            </div>

            <div className="event-filters">
              <select
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Events ({allEvents.length})</option>
                <option value="anniversaries">This Year's Anniversaries ({calculateAnniversaries.length})</option>
                <option value="historical">Historical Events ({extractDates.length})</option>
                <option value="birth">Births & Birthdays ({allEvents.filter(e => e.type === 'birth' || e.type === 'birth-anniversary').length})</option>
                <option value="death">Deaths & Memorials ({allEvents.filter(e => e.type === 'death' || e.type === 'death-anniversary').length})</option>
                <option value="marriage">Marriages & Anniversaries ({allEvents.filter(e => e.type === 'marriage' || e.type === 'marriage-anniversary').length})</option>
              </select>
            </div>

            <button className="today-btn" onClick={goToToday}>
              Today
            </button>
          </div>
        </div>

        <div className="calendar-content">
          {viewMode === 'upcoming' && renderUpcomingView()}
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'year' && renderYearView()}
          {viewMode === 'timeline' && renderTimelineView()}
        </div>

        {/* Show legend only for Month, Year, and Timeline views (Upcoming has its own) */}
        {(viewMode === 'month' || viewMode === 'year' || viewMode === 'timeline') && (
          <div className="calendar-legend">
            <div className="legend-items">
              <div className="legend-item">
                <span className="legend-icon birth">🎂</span>
                <span>Births</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon death">🕊️</span>
                <span>Deaths</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon marriage">💒</span>
                <span>Marriages</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon birth-anniversary">🎉</span>
                <span>Birthdays</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon death-anniversary">🌹</span>
                <span>Memorials</span>
              </div>
              <div className="legend-item">
                <span className="legend-icon marriage-anniversary">💕</span>
                <span>Wedding Anniversaries</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyCalendar;
